import React, { useState, useEffect } from 'react'
import {
  Settings,
  ExternalLink,
  BarChart3,
  Calculator,
  FileText,
  Shield,
  Users
} from 'lucide-react'
import { useCurrentUser } from '@/contexts/UserContext'

export function ConfiguracoesTab() {
  const { currentUser: user, isLoading } = useCurrentUser();
  const [error, setError] = useState<string | null>(null);

  // Check for user errors
  useEffect(() => {
    if (!isLoading && !user) {
      setError('Usuário não encontrado');
    } else {
      setError(null);
    }
  }, [isLoading, user]);

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-brand-gray-light font-body">Carregando configurações...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl"></span>
            </div>
            <p className="text-red-400 font-body mb-2">Erro ao carregar configurações</p>
            <p className="text-brand-gray-light font-body text-sm">{error || 'Dados do usuário não disponíveis'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      {/* Configurações de Perfil */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          <div className="p-2 rounded-monumenta bg-brand-primary/10">
            <Settings className="w-6 h-6 text-brand-primary" />
          </div>
          Configurações de Perfil
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h4 className="font-display text-lg font-semibold text-white">Informações Pessoais</h4>
            
            <div className="space-y-4">
              <div>
                <label className="form-label">
                  Nome Completo
                </label>
                <input 
                  type="text" 
                  defaultValue={user.name}
                  className="form-input w-full"
                />
              </div>
              
              <div>
                <label className="form-label">
                  Email
                </label>
                <input 
                  type="email" 
                  defaultValue={user.email}
                  className="form-input w-full"
                />
              </div>
              
              <div>
                <label className="form-label">
                  Telefone
                </label>
                <input 
                  type="tel" 
                  defaultValue="(11) 99999-1234"
                  className="form-input w-full"
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <h4 className="font-display text-lg font-semibold text-white">Configurações do Sistema</h4>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-brand-dark/30 rounded-monumenta hover:bg-brand-dark-lighter/50 transition-colors duration-200">
                <div>
                  <p className="text-white font-display font-medium">Notificações por Email</p>
                  <p className="text-brand-gray-light text-sm font-body">Receber alertas de performance e aprovações</p>
                </div>
                <div className="w-12 h-6 bg-brand-primary rounded-monumenta relative cursor-pointer shadow-monumenta">
                  <div className="w-5 h-5 bg-white rounded-monumenta absolute right-0.5 top-0.5 shadow transition-all duration-200"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-brand-dark/30 rounded-monumenta hover:bg-brand-dark-lighter/50 transition-colors duration-200">
                <div>
                  <p className="text-white font-display font-medium">Relatórios Semanais</p>
                  <p className="text-brand-gray-light text-sm font-body">Resumo semanal da equipe por email</p>
                </div>
                <div className="w-12 h-6 bg-brand-dark-lighter rounded-monumenta relative cursor-pointer shadow-inner">
                  <div className="w-5 h-5 bg-white rounded-monumenta absolute left-0.5 top-0.5 shadow transition-all duration-200"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-brand-dark/30 rounded-monumenta hover:bg-brand-dark-lighter/50 transition-colors duration-200">
                <div>
                  <p className="text-white font-display font-medium">Sincronização Qulture</p>
                  <p className="text-brand-gray-light text-sm font-body">Integração automática com sistema Qulture</p>
                </div>
                <div className="w-12 h-6 bg-brand-primary rounded-monumenta relative cursor-pointer shadow-monumenta">
                  <div className="w-5 h-5 bg-white rounded-monumenta absolute right-0.5 top-0.5 shadow transition-all duration-200"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-6 border-t border-gray-700/50 mt-8">
          <div className="flex gap-4">
            <button className="btn-primary">
              Salvar Alterações
            </button>
            <button className="btn-ghost">
              Cancelar
            </button>
          </div>
        </div>
      </div>

      {/* Integrações */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          <div className="p-2 rounded-monumenta bg-brand-primary/10">
            <ExternalLink className="w-6 h-6 text-brand-primary" />
          </div>
          Integrações de Sistema
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card-interactive group p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-blue/80 rounded-monumenta flex items-center justify-center shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-200">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-white font-display font-semibold">Qulture</h4>
                <p className="text-success text-sm font-body flex items-center gap-1">
                  <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
                  Conectado
                </p>
              </div>
            </div>
            <p className="text-brand-gray-light text-sm font-body mb-4 leading-relaxed">
              Sistema de gestão de performance e feedback contínuo
            </p>
            <button className="btn-ghost text-xs w-full">
              Configurar
            </button>
          </div>

          <div className="card-interactive group p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-success to-success/80 rounded-monumenta flex items-center justify-center shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-200">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-white font-display font-semibold">Proteus</h4>
                <p className="text-success text-sm font-body flex items-center gap-1">
                  <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
                  Conectado
                </p>
              </div>
            </div>
            <p className="text-brand-gray-light text-sm font-body mb-4 leading-relaxed">
              Sistema financeiro para folha de pagamento e custos
            </p>
            <button className="btn-ghost text-xs w-full">
              Configurar
            </button>
          </div>

          <div className="card-interactive group p-6 border-warning/30 bg-warning/5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-brand-gray to-brand-gray/80 rounded-monumenta flex items-center justify-center shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-200">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-white font-display font-semibold">WRIKE</h4>
                <p className="text-warning text-sm font-body flex items-center gap-1">
                  <span className="w-2 h-2 bg-warning rounded-full animate-pulse"></span>
                  Em configuração
                </p>
              </div>
            </div>
            <p className="text-brand-gray-light text-sm font-body mb-4 leading-relaxed">
              Sistema de gestão de projetos e alocação de recursos
            </p>
            <button className="btn-primary text-xs w-full">
              Conectar
            </button>
          </div>
        </div>
      </div>

      {/* Configurações Avançadas */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          <div className="p-2 rounded-monumenta bg-brand-primary/10">
            <Settings className="w-6 h-6 text-brand-primary" />
          </div>
          Configurações Avançadas
        </h3>
        
        <div className="space-y-8">
          <div>
            <h4 className="font-display text-lg font-semibold text-white mb-4">Exportação de Dados</h4>
            <p className="text-brand-gray-light text-sm font-body mb-6 leading-relaxed">
              Configure formatos e frequência de exportação dos relatórios
            </p>
            
            <div className="flex gap-4">
              <button className="btn-ghost text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Exportar Dados
              </button>
              <button className="btn-ghost text-sm">
                Configurar Backup
              </button>
            </div>
          </div>

          <div className="border-t border-gray-700/50 pt-8">
            <h4 className="font-display text-lg font-semibold text-white mb-6">Compliance & Segurança</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card-interactive group p-4 border-success/30 bg-success/5">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-success group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-white font-display font-semibold">LGPD</span>
                  <span className="text-success text-sm font-body bg-success/20 px-2 py-1 rounded-monumenta">Conforme</span>
                </div>
                <p className="text-brand-gray-light text-xs font-body leading-relaxed">
                  Sistema em conformidade com Lei Geral de Proteção de Dados
                </p>
              </div>
              
              <div className="card-interactive group p-4 border-success/30 bg-success/5">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-6 h-6 text-success group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-white font-display font-semibold">CLT</span>
                  <span className="text-success text-sm font-body bg-success/20 px-2 py-1 rounded-monumenta">Atualizado</span>
                </div>
                <p className="text-brand-gray-light text-xs font-body leading-relaxed">
                  Cálculos atualizados com Consolidação das Leis do Trabalho
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}